import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api, mediaUrl } from '../lib/api'
import { useAuth } from '../lib/auth'

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = () => reject(new Error('Failed to read image'))
    reader.readAsDataURL(file)
  })
}

export function ProfilePage() {
  const { token, user, wallet, refresh, profileComplete } = useAuth()
  const [params] = useSearchParams()
  const nav = useNavigate()
  const setupMode = params.get('setup') === '1'
  const welcome = params.get('welcome') === '1'
  const fileRef = useRef<HTMLInputElement>(null)
  const [displayName, setDisplayName] = useState(user?.displayName || '')
  const [bio, setBio] = useState('')
  const [avatarUrl, setAvatarUrl] = useState<string | null>(user?.avatarUrl || null)
  const [preview, setPreview] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(
    welcome ? 'Account created — you have 500 demo credits. Add a photo if you want, then continue.' : null
  )
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    ;(async () => {
      try {
        const me = await api<{
          user: { displayName: string; avatarUrl?: string | null }
          profile: { bio?: string | null; avatarUrl?: string | null } | null
          wallet?: { available: number; currency: string }
        }>('/api/me', { token })
        setDisplayName(me.user.displayName || '')
        setBio(me.profile?.bio || '')
        setAvatarUrl(me.user.avatarUrl || me.profile?.avatarUrl || null)
      } catch (e: any) {
        setError(e.message)
      }
    })()
  }, [token])

  async function saveProfile(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    setMsg(null)
    try {
      const res = await api<{
        user: { displayName: string; avatarUrl?: string | null; profileComplete?: boolean }
        profile: { bio?: string | null; avatarUrl?: string | null; setupComplete?: boolean } | null
      }>('/api/me/profile', {
        method: 'PATCH',
        token,
        body: {
          displayName: displayName.trim(),
          bio,
          setupComplete: true,
        },
      })
      setDisplayName(res.user.displayName)
      setBio(res.profile?.bio || '')
      setAvatarUrl(res.user.avatarUrl || res.profile?.avatarUrl || null)
      await refresh()
      setMsg('Profile saved to your account')
      if (setupMode) {
        nav('/', { replace: true })
      }
    } catch (err: any) {
      setError(err.message || 'Could not save profile')
    } finally {
      setBusy(false)
    }
  }

  async function onPickFile(file: File | null) {
    if (!file) return
    setError(null)
    setMsg(null)
    if (!file.type.startsWith('image/')) {
      setError('Choose a jpeg, png, webp, or gif image')
      return
    }
    if (file.size > 900_000) {
      setError('Image must be under ~900KB — try a smaller crop')
      return
    }
    try {
      setBusy(true)
      const dataUrl = await fileToDataUrl(file)
      setPreview(dataUrl)
      const res = await api<{
        user: { avatarUrl?: string | null }
        profile: { avatarUrl?: string | null } | null
      }>('/api/me/profile/avatar', {
        method: 'POST',
        token,
        body: { image: dataUrl },
      })
      setAvatarUrl(res.user.avatarUrl || res.profile?.avatarUrl || null)
      setPreview(null)
      // Mark setup complete after avatar if they already have a name
      if (displayName.trim().length >= 2) {
        await api('/api/me/profile', {
          method: 'PATCH',
          token,
          body: { displayName: displayName.trim(), setupComplete: true },
        })
      }
      await refresh()
      setMsg('Profile picture saved')
    } catch (err: any) {
      setError(err.message || 'Upload failed')
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const shown = preview || mediaUrl(avatarUrl)

  return (
    <div className="stack">
      <section className="hero-fight" style={{ minHeight: 150 }}>
        <div
          className="bg"
          style={{
            backgroundImage: 'url(/assets/shell/trophy.png)',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: '90% center',
            opacity: 0.3,
          }}
        />
        <div className="veil" />
        <div className="content">
          <div className="eyebrow">{setupMode ? 'Finish joining' : 'Your card'}</div>
          <h1>{setupMode ? 'Set up your profile' : 'Profile'}</h1>
          <div className="meta">
            <span>@{user?.username}</span>
            {wallet && (
              <span>
                {wallet.available} {wallet.currency} available
              </span>
            )}
            <span>{profileComplete ? 'Saved account' : 'Name required'}</span>
          </div>
        </div>
      </section>

      {setupMode && (
        <div className="panel">
          <p style={{ margin: 0 }}>
            Your <strong>username + password</strong> are stored so you can log in next time. Set how
            friends see you (display name). A profile picture is optional but recommended for the
            pool board.
          </p>
        </div>
      )}

      <div className="panel profile-panel">
        <div className="profile-avatar-col">
          <div className="profile-avatar-wrap">
            {shown ? (
              <img className="profile-avatar" src={shown} alt="Your avatar" />
            ) : (
              <div className="profile-avatar placeholder">
                {(displayName || user?.username || '?').slice(0, 1).toUpperCase()}
              </div>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            hidden
            onChange={(e) => onPickFile(e.target.files?.[0] || null)}
          />
          <button
            type="button"
            className="btn secondary"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
            data-testid="upload-avatar"
          >
            {busy ? 'Working…' : 'Upload picture'}
          </button>
          <p className="muted" style={{ fontSize: 12, margin: '6px 0 0', textAlign: 'center' }}>
            jpeg / png / webp · under 900KB
          </p>
        </div>

        <form className="profile-form" onSubmit={saveProfile}>
          <label>
            Display name <span className="muted">(required)</span>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={64}
              minLength={2}
              required
              placeholder="How friends see you"
              data-testid="display-name"
            />
          </label>
          <label>
            Bio <span className="muted">(optional)</span>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={280}
              rows={3}
              placeholder="Trash talk optional"
            />
          </label>
          {error && <div className="error">{error}</div>}
          {msg && <div className="success">{msg}</div>}
          <div className="row">
            <button
              className="btn"
              type="submit"
              disabled={busy || displayName.trim().length < 2}
              data-testid="save-profile"
            >
              {setupMode ? 'Save & enter lobby' : 'Save profile'}
            </button>
            {setupMode && profileComplete && (
              <Link className="btn secondary" to="/">
                Skip to lobby
              </Link>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}
